import React, { useState } from "react";
import { Slider, Handles, Tracks, Rail } from "react-compound-slider";
import { Handle, SliderRail, Track } from "../../components/RangeSlider";
import { updateSetting, useSettings } from "../../ducks/settings";
import {
  chooseGame,
  setCustomGame,
  showCustomPopup,
  useLobby,
} from "../../ducks/lobby";
import styles from "./styles/CreateCustomGame.module.css";
import Popup from "../../components/Popup";
import useTranslation from "next-translate/useTranslation";
import { VARIANT_CHESS, VARIANT_CHESS960 } from "@chesshotel/constants";
import { getModeId } from "../../util/util";

export default function CustomGamePopup() {
  const { t } = useTranslation("common");

  const { minOpponentRating, maxOpponentRating } = useSettings();

  const { customTime, customInc, customVariant } = useLobby();
  const time = customTime ? customTime : 10;
  const inc = customInc ? customInc : 0;
  const variant = customVariant ? customVariant : VARIANT_CHESS;

  const [chosenTime, setChosenTime] = useState(time);
  const [chosenInc, setChosenInc] = useState(inc);
  const [chosenVariant, setChosenVariant] = useState(variant);

  const [minRating, setMinRating] = useState(minOpponentRating);
  const [maxRating, setMaxRating] = useState(maxOpponentRating);

  const handleTimeChange = (event) => {
    setChosenTime(parseInt(event.target.value));
  };

  const handleIncrementChange = (event) => {
    setChosenInc(parseInt(event.target.value));
  };

  const handleVariantChange = (event) => {
    setChosenVariant(parseInt(event.target.value));
  };

  const handleSliderUpdate = (values) => {
    setMinRating(values[0]);
    setMaxRating(values[1]);
  };

  const handleSliderChange = (values) => {
    updateSetting("minOpponentRating", values[0]);
    updateSetting("maxOpponentRating", values[1]);
  };

  const handleSaveCustom = () => {
    const modeId = getModeId(chosenTime, chosenVariant);
    chooseGame({
      time: chosenTime,
      inc: chosenInc,
      variant: chosenVariant,
      modeId,
    });

    try {
      localStorage.setItem("customTime", chosenTime.toString());
      localStorage.setItem("customInc", chosenInc.toString());
      localStorage.setItem("customVariant", chosenVariant.toString());
      localStorage.setItem("chosenTime", chosenTime.toString());
      localStorage.setItem("chosenInc", chosenInc.toString());
      localStorage.setItem("chosenVariant", chosenVariant.toString());
    } catch (e) {}

    setCustomGame({ time: chosenTime, inc: chosenInc, variant: chosenVariant });
    showCustomPopup(false);
  };

  const defaultValues = [minOpponentRating, maxOpponentRating];

  return (
    <Popup title={t("Custom")} onClose={() => showCustomPopup(false)}>
      <div className={styles.createGameCont}>
        <div className={styles.createGame}>
          <label className="text-sm mb-0.5">{t("Variant")}</label>
          <select onChange={handleVariantChange} defaultValue={variant}>
            <option value={VARIANT_CHESS}>{t("Standard")}</option>
            <option value={VARIANT_CHESS960}>{t("Chess960")}</option>
          </select>
          <label className="text-sm mt-4 mb-0.5">{t("Time in minutes")}</label>
          <select onChange={handleTimeChange} defaultValue={time}>
            <option value="1">1</option>
            <option value="2">2</option>
            <option value="3">3</option>
            <option value="4">4</option>
            <option value="5">5</option>
            <option value="6">6</option>
            <option value="7">7</option>
            <option value="8">8</option>
            <option value="9">9</option>
            <option value="10">10</option>
            <option value="11">11</option>
            <option value="12">12</option>
            <option value="13">13</option>
            <option value="14">14</option>
            <option value="15">15</option>
            <option value="16">16</option>
            <option value="17">17</option>
            <option value="18">18</option>
            <option value="19">19</option>
            <option value="20">20</option>
            <option value="25">25</option>
            <option value="30">30</option>
            <option value="35">35</option>
            <option value="40">40</option>
            <option value="45">45</option>
            <option value="50">50</option>
            <option value="55">55</option>
            <option value="60">60</option>
          </select>

          <label className="text-sm mt-4 mb-0.5">
            {t("Increment in seconds")}
          </label>
          <select onChange={handleIncrementChange} defaultValue={inc}>
            <option value="0">0</option>
            <option value="1">1</option>
            <option value="2">2</option>
            <option value="3">3</option>
            <option value="4">4</option>
            <option value="5">5</option>
            <option value="6">6</option>
            <option value="7">7</option>
            <option value="8">8</option>
            <option value="9">9</option>
            <option value="10">10</option>
          </select>

          <label
            className="text-sm mt-4 mb-0.5"
            style={{ marginBottom: "10px" }}
          >
            {t("Opponent rating")}:
            <span
              style={{ fontWeight: "bolder" }}
            >{` ${minRating} - ${maxRating}`}</span>
          </label>
          <Slider
            step={10}
            mode={5}
            domain={[0, 3000]}
            rootStyle={{
              position: "relative",
              marginLeft: "12px",
              marginRight: "12px",
              marginTop: "12px",
              marginBottom: "22px",
            }}
            onUpdate={handleSliderUpdate}
            onChange={handleSliderChange}
            values={defaultValues}
          >
            <Rail>
              {({ getRailProps }) => <SliderRail getRailProps={getRailProps} />}
            </Rail>
            <Handles>
              {({ handles, getHandleProps }) => (
                <div className="slider-handles">
                  {handles.map((handle, i) => (
                    <Handle
                      key={handle.id}
                      handle={handle}
                      domain={[0, 3000]}
                      getHandleProps={getHandleProps}
                    />
                  ))}
                </div>
              )}
            </Handles>
            <Tracks left={false} right={false}>
              {({ tracks, getTrackProps }) => (
                <div className="slider-tracks">
                  {tracks.map(({ id, source, target }) => (
                    <Track
                      key={id}
                      source={source}
                      target={target}
                      getTrackProps={getTrackProps}
                    />
                  ))}
                </div>
              )}
            </Tracks>
          </Slider>
          <div className="flex justify-end items-center w-full mt-4 pt-4">
            <button
              className="btn btn-base btn-outline"
              onClick={() => showCustomPopup(false)}
            >
              {t("Cancel")}
            </button>
            <button
              className="btn btn-base btn-blue ml-3"
              onClick={handleSaveCustom}
            >
              {t("Save")}
            </button>
          </div>
        </div>
      </div>
    </Popup>
  );
}
